/**
 * `Idempotency-Key`: **gerada e reusada em retentativa de escrita** (`API §3`).
 *
 * O ponto que faz este arquivo existir e uma frase do briefing: *"se o usuario aperta duas
 * vezes, e a mesma chave"*. Uma chave gerada dentro da funcao que faz o `fetch` nao consegue
 * isso — cada toque criaria uma chave nova, e duas chaves novas sao dois pedidos. A chave
 * precisa estar amarrada a **intencao**, que vive mais do que a requisicao.
 *
 * O escopo e essa intencao. Enquanto a intencao nao se resolveu, `keyFor` devolve sempre a
 * mesma chave:
 *
 * ```text
 *  toque 1 -> keyFor('POST /v1/orders') -> k1 -> requisicao em voo
 *  toque 2 -> keyFor('POST /v1/orders') -> k1 -> o servidor responde 409 IDEMPOTENCY_IN_PROGRESS
 *  429 -> retentativa                   -> k1 -> mesma chave, por construcao
 *  201 -> release('POST /v1/orders')          -> a proxima intencao ganha k2
 * ```
 *
 * **Quando a chave e liberada** (`API §3.2`): quando o resultado e **determinístico** — `2xx`
 * ou `4xx` de negocio, que o servidor devolveria igual no replay. Em `5xx` e em falha de rede
 * a chave **fica**: *"Chave conhecida, requisicao original falhou com 5xx: reprocessa"*, e e
 * justamente reapresentando a mesma chave que o cliente nao cria um segundo efeito.
 */
import { IdempotencyKeySchema } from '@farmacia/contracts';

/**
 * O cofre de chaves da sessao. Vive no cliente, e nao na tela: nenhuma tela deve lembrar de
 * gerar, guardar ou liberar chave (o mesmo criterio do CSRF).
 */
export class IdempotencyKeeper {
  readonly #keys = new Map<string, string>();
  readonly #generate: () => string;

  constructor(generate: () => string) {
    this.#generate = generate;
  }

  /** A chave desta intencao. Gera na primeira chamada; devolve a mesma ate `release`. */
  keyFor(scope: string): string {
    const existing = this.#keys.get(scope);
    if (existing !== undefined) return existing;

    const generated = this.#generate();
    const parsed = IdempotencyKeySchema.safeParse(generated);
    if (!parsed.success) {
      // Falha alto: `API §3.2` recusa chave fora do formato com `400`, e uma chave malformada
      // que so falha no servidor viraria um bug intermitente de rede aos olhos de quem depura.
      throw new TypeError('generateIdempotencyKey deve produzir um UUID (API §3.2).');
    }

    this.#keys.set(scope, parsed.data);
    return parsed.data;
  }

  /** A intencao se resolveu de forma determinística. A proxima ganha chave nova. */
  release(scope: string): void {
    this.#keys.delete(scope);
  }

  /** Logout / troca de conta: nenhuma chave sobrevive a troca de titular. */
  clear(): void {
    this.#keys.clear();
  }

  /** So para teste e diagnostico. */
  get size(): number {
    return this.#keys.size;
  }
}

/**
 * O escopo default de uma requisicao: `METODO caminho`.
 *
 * Serve as transicoes do Painel (`POST /v1/store/orders/{id}/accept` — o caminho ja carrega o
 * pedido) e ao `POST /v1/orders` (uma intencao de pedido por vez, por titular). Quando a
 * intencao e mais fina que o caminho, quem chama passa o escopo explicito.
 */
export function defaultIdempotencyScope(method: string, path: string): string {
  return `${method.toUpperCase()} ${path}`;
}
